import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Pencil, X, Check, Info } from 'lucide-react';
import {
  CrewHoursBalancerScenarios,
  FineTuneQuoteSliders,
} from '../../components/ui/CrewHoursBalancer';
import { useUserProfile } from '../../context/UserProfileContext';
import { useSharedQuote } from '../../context/QuoteContext';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import { useOrgRates } from '../../hooks/useOrgRates';
import { useResidentialFlatRates } from '../../hooks/useResidentialFlatRates';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { RateCardHint } from '../../components/ui/RateCardHint';
import { ReadOnlyBanner } from '../../components/ui/ReadOnlyBanner';
import { TaxBreakdown } from '../../components/ui/TaxBreakdown';
import { currencySymbol, QuoteCrewMember, QuoteFormula, ResourceSlug } from '../../types/admin';
import { buildCrewFromFormula, buildCrewFromOrgRates, ceilQuarter, computeFormula, getShiftLengthHours, getShuttleTrucksAndDrivers } from '../../utils/formulaCalculation';
import { checkFormulaReadiness, getFormulaFingerprint } from '../../utils/formulaReadiness';
import { computeQuoteMaterials, type QuoteMaterialsSummary } from '../../lib/inventoryPricing';
import { resolveOrgTravelTimeRate, resolveOrgEfficiencyMultiplier, resolveOrgFixedTravelTimeHours } from '@iomi/shared';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(n: number, decimals = 2): string {
  return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: decimals });
}

function fmtHours(n: number): string {
  return `${fmt(n, 1)} hr${n !== 1 ? 's' : ''}`;
}

function parseQty(s: string): number {
  return Math.max(0, parseInt(s, 10) || 0);
}

function parseHours(s: string): number {
  return Math.max(0, parseFloat(s) || 0);
}

function parseRate(s: string): number {
  return Math.max(0, parseFloat(s) || 0);
}

// ── Types for edit state ──────────────────────────────────────────────────────

type CrewEditEntry = {
  slug: ResourceSlug;
  name: string;
  qtyStr: string;
  rateStr: string;
};

type HoursEditState = {
  actualJobDurationStr: string;
  finalTransitTimeHoursStr: string;
  travelTimeToJobHoursStr: string;
};

// ── Breakdown card ────────────────────────────────────────────────────────────

function BreakdownCard({
  title,
  headerValue,
  action,
  children,
}: {
  title: string;
  headerValue: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
      <div className="px-4 py-3.5 border-b border-stone flex items-center gap-2">
        <p className="font-semibold text-small text-midnight flex-1 min-w-0">{title}</p>
        {action}
        <p className="font-bold text-small text-plum tabular-nums shrink-0">{headerValue}</p>
      </div>
      <div className="px-4 py-2 flex flex-col">
        {children}
      </div>
    </div>
  );
}

function BreakdownRow({
  label,
  right,
  sub,
}: {
  label: string;
  right: string;
  sub?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5 border-b border-stone last:border-0">
      <div className="flex-1 min-w-0">
        <p className="text-small font-medium text-midnight">{label}</p>
        {sub && <p className="text-[11px] text-slate mt-0.5">{sub}</p>}
      </div>
      <p className="shrink-0 text-small font-semibold text-midnight tabular-nums">{right}</p>
    </div>
  );
}

function EditButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="shrink-0 flex items-center gap-1 h-7 px-2 rounded-lg border border-stone
                 text-[12px] font-semibold text-slate bg-white
                 hover:border-plum/40 hover:text-plum active:bg-mist
                 transition-colors duration-150"
    >
      <Pencil size={11} />
      Edit
    </button>
  );
}

function SaveCancelRow({
  onSave,
  onCancel,
  saving,
  error,
}: {
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  error: string;
}) {
  return (
    <div className="flex flex-col gap-2 pt-2.5 pb-1">
      {error && <p className="text-[12px] text-rose">{error}</p>}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 h-9 rounded-xl border border-stone text-[13px] font-semibold text-slate
                     hover:border-rose/40 hover:text-rose transition-colors duration-150"
        >
          <X size={13} className="inline mr-1 -mt-0.5" />
          Cancel
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="flex-1 h-9 rounded-xl bg-plum text-white text-[13px] font-semibold
                     disabled:opacity-50 hover:bg-plum/90 transition-opacity duration-150"
        >
          {saving ? 'Saving…' : <><Check size={13} className="inline mr-1 -mt-0.5" />Save</>}
        </button>
      </div>
    </div>
  );
}

// ── Inline editable input (shared style) ─────────────────────────────────────

function InlineInput({
  value,
  onChange,
  width,
  step = '1',
  min = '0',
}: {
  value: string;
  onChange: (v: string) => void;
  width: string;
  step?: string;
  min?: string;
}) {
  return (
    <input
      type="number"
      step={step}
      min={min}
      value={value}
      onChange={e => onChange(e.target.value)}
      className={`${width} h-9 rounded-lg border border-stone bg-mist text-center
                  text-[13px] font-semibold tabular-nums text-midnight
                  focus:outline-none focus:border-plum focus:ring-1 focus:ring-plum/30`}
    />
  );
}

// ── Materials card ───────────────────────────────────────────────────────────
// One flat line on the subtotal. The estimator can override the total; a full
// recalculation (inventory, rental length, multiplier…) restores the
// auto-calculated amount.

function MaterialsCard({
  f,
  symbol,
  isDraft,
  auto,
  updateQuote,
}: {
  f: QuoteFormula;
  symbol: string;
  isDraft: boolean;
  auto: QuoteMaterialsSummary;
  updateQuote: (patch: { formula: QuoteFormula }) => Promise<unknown>;
}) {
  const [editing, setEditing] = useState(false);
  const [totalStr, setTotalStr] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const current = f.materialsCost ?? 0;
  const count = f.materialsCount ?? auto.listedCount;
  const isAdjusted = Math.abs(current - auto.total) >= 0.005;
  const liveTotal = editing ? Math.max(0, parseFloat(totalStr) || 0) : current;

  function startEdit() {
    setTotalStr(current.toFixed(2));
    setError('');
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setError('');
  }

  async function saveEdit() {
    setSaving(true);
    setError('');
    try {
      const next = Math.round(Math.max(0, parseFloat(totalStr) || 0) * 100) / 100;
      // finalCostEstimate always includes materials, so peel the old amount off first.
      const laborCost = f.finalCostEstimate - current;
      await updateQuote({
        formula: { ...f, materialsCost: next, finalCostEstimate: laborCost + next },
      });
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <BreakdownCard
      title="Materials"
      headerValue={`${symbol}${fmt(liveTotal, 2)}`}
      action={isDraft && !editing ? <EditButton onClick={startEdit} /> : undefined}
    >
      {editing ? (
        <>
          <div className="flex items-center gap-4 py-2.5">
            <div className="flex-1 min-w-0">
              <p className="text-small font-medium text-midnight">Materials (×{count})</p>
              <p className="text-[11px] text-slate mt-0.5">Auto-calculated: {symbol}{fmt(auto.total, 2)}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <span className="text-[12px] text-slate">{symbol}</span>
              <InlineInput value={totalStr} onChange={setTotalStr} width="w-24" step="0.01" />
            </div>
          </div>
          <p className="text-[11px] text-slate pb-1">
            Resets to the auto-calculated total whenever the estimate is recalculated.
          </p>
          <SaveCancelRow onSave={saveEdit} onCancel={cancelEdit} saving={saving} error={error} />
        </>
      ) : (
        <BreakdownRow
          label={`Materials (×${count})`}
          sub={isAdjusted
            ? `Adjusted · auto-calculated ${symbol}${fmt(auto.total, 2)}`
            : auto.unpricedCount > 0
              ? `${auto.pricedCount} priced item${auto.pricedCount !== 1 ? 's' : ''} · ${auto.unpricedCount} without a cost`
              : "Packing items + equipment at the move's rental length"}
          right={`${symbol}${fmt(current, 2)}`}
        />
      )}
    </BreakdownCard>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function QuoteBreakdownPage() {
  const { quoteId } = useParams<{ quoteId: string }>();
  const navigate = useNavigate();
  const { activeOrg } = useUserProfile();
  const orgId = activeOrg?.orgId ?? null;
  const { quote, loading, updateQuote } = useSharedQuote();
  const { org } = useOrgSettings(orgId ?? '');
  const { orgItems } = useSharedQuote();
  const { rates: orgRates } = useOrgRates(orgId ?? '');
  const { flatRates: orgFlatRates, loading: orgFlatRatesLoading } = useResidentialFlatRates(orgId ?? '');
  const isOrgAdmin = activeOrg?.role === 'admin' || activeOrg?.role === 'field_lead';
  const backUrl = `/moves/${quoteId}`;

  // ── Efficiency multiplier edit state
  const [editingMultiplier, setEditingMultiplier] = useState(false);
  const [multiplierStr, setMultiplierStr] = useState('');
  const [savingMultiplier, setSavingMultiplier] = useState(false);
  const [multiplierError, setMultiplierError] = useState('');

  // ── Crew / rates edit state
  const [editingRates, setEditingRates] = useState(false);
  const [crewEdits, setCrewEdits] = useState<CrewEditEntry[]>([]);
  const [savingRates, setSavingRates] = useState(false);
  const [ratesError, setRatesError] = useState('');

  // ── Hours edit state
  const [editingHours, setEditingHours] = useState(false);
  const [hoursEdits, setHoursEdits] = useState<HoursEditState>({
    actualJobDurationStr: '',
    finalTransitTimeHoursStr: '',
    travelTimeToJobHoursStr: '',
  });
  const [savingHours, setSavingHours] = useState(false);
  const [hoursError, setHoursError] = useState('');
  // Balancer save state lives up here with the other hooks: nothing below the
  // early returns may call a hook, or React throws "Rendered more hooks…" once
  // the quote finishes loading.
  const [savingBalance, setSavingBalance] = useState(false);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="px-4 pt-6 flex flex-col items-center py-20 gap-2 text-center">
        <p className="text-small text-rose font-semibold">Quote not found</p>
      </div>
    );
  }

  const f = quote.formula;
  const symbol = currencySymbol(org?.defaultCurrency);
  const isDraft = quote.quoteStatus === 'draft';

  // Which rates (if any) are $0 for this quote — drives the warning rows below
  // and explains an empty estimate when the rate card is the only blocker.
  const readiness = checkFormulaReadiness(quote, {
    effectiveCrew: quote.crew ?? buildCrewFromOrgRates(orgRates),
    useBlendedTruckDriverRate: org?.useBlendedTruckDriverRate,
    residentialPricingModel: org?.residentialPricingModel,
    flatRates: orgFlatRatesLoading ? undefined : orgFlatRates,
  });

  if (!f) {
    return (
      <div className="px-4 pt-6 pb-10 md:mx-auto md:max-w-3xl md:px-6">
        <button
          type="button"
          onClick={() => navigate(backUrl)}
          className="flex items-center gap-1 text-small text-slate hover:text-midnight mb-6
                     transition-colors -ml-1"
        >
          <ChevronLeft size={16} />
          {quote.customerCompanyName}
        </button>
        <div className="flex flex-col items-center py-16 gap-3 text-center">
          <p className="text-small font-medium text-midnight">No estimate available yet</p>
          <p className="text-[13px] text-slate leading-snug max-w-xs">
            {readiness.onlyMissingRateCard
              ? 'The estimate will calculate automatically once the rate card is set.'
              : 'Open the Quote Details page to trigger an automatic calculation.'}
          </p>
          <RateCardHint readiness={readiness} isOrgAdmin={isOrgAdmin} className="max-w-xs" />
        </div>
      </div>
    );
  }

  // ── Crew size constraints ────────────────────────────────────────────────
  const isResQuote = quote.type === 'residential';
  // Residential: no separate drivers (supervisor drives).
  const driverQty = isResQuote ? 0 : (f.driverQuantity ?? f.truckQuantity);
  const shuttleTableDrivers = f.method === 'Shuttle'
    ? getShuttleTrucksAndDrivers(quote.addresses?.transit?.durationSeconds ?? 0).drivers
    : 0;
  const maxCrew = isResQuote
    ? Math.max(f.finalCrewSize + 2, 8)
    : (f.method === 'Shuttle'
        ? Math.max(1, f.combinedOptimumCrewSize + shuttleTableDrivers)
        : (f.method === 'Internal'
            ? Math.max(1, f.originOptimumCrewSize)
            : Math.max(1,
                f.destinationOptimumCrewSize > 0
                  ? Math.min(f.originOptimumCrewSize, f.destinationOptimumCrewSize)
                  : f.originOptimumCrewSize,
              )));
  const minCrew = isResQuote
    ? 2 // 1 supervisor + 1 mover minimum
    : (f.method === 'Shuttle'
        ? Math.max(1, f.truckQuantity + 3)
        : Math.max(1, f.truckQuantity)); // Load & Go / Internal
  const shiftLengthHours = quote.movingDate?.shiftLengthHours ?? getShiftLengthHours(quote.movingDate?.timing);
  // Min on-site hours = what you'd get at max crew
  const minActualHours = ceilQuarter(maxCrew > 0 ? f.manHours / maxCrew : 0);

  // ── Rates edit handlers ───────────────────────────────────────────────────

  function startEditRates() {
    const crewWithQty = buildCrewFromFormula(f!, quote!.crew ?? [], quote!.type, org?.useBlendedTruckDriverRate);
    setCrewEdits(crewWithQty.map(m => ({
      slug: m.slug,
      name: m.name,
      qtyStr: String(m.quantity),
      rateStr: String(m.hourlyRate),
    })));
    setRatesError('');
    setEditingRates(true);
  }

  function cancelEditRates() {
    setEditingRates(false);
    setRatesError('');
  }

  function updateCrewEntry(index: number, field: 'qtyStr' | 'rateStr', value: string) {
    setCrewEdits(prev => prev.map((e, i) => i === index ? { ...e, [field]: value } : e));
  }

  async function saveRates() {
    setSavingRates(true);
    setRatesError('');
    try {
      const parsed = crewEdits.map(m => ({
        ...m,
        quantity: parseQty(m.qtyStr),
        hourlyRate: parseRate(m.rateStr),
      }));
      const newTotalHourlyRate = parsed.reduce((sum, m) => sum + m.quantity * m.hourlyRate, 0);
      const rawCrewSize = parsed.filter(m => m.slug !== 'truck').reduce((sum, m) => sum + m.quantity, 0);
      const newFinalCrewSize = Math.max(minCrew, Math.min(maxCrew, rawCrewSize));
      // Recalculate actual hours based on new crew size (manHours / crewSize)
      const newActualJobDuration = ceilQuarter(newFinalCrewSize > 0 ? f!.manHours / newFinalCrewSize : 0);
      const newTotalHours = ceilQuarter(newActualJobDuration + f!.finalTransitTimeHours + f!.travelTimeToJobHours);
      const newFinalCostEstimate = newTotalHourlyRate * newTotalHours + (f!.materialsCost ?? 0);
      const updatedCrew: QuoteCrewMember[] = parsed.map(m => ({
        slug: m.slug,
        name: m.name,
        hourlyRate: m.hourlyRate,
        quantity: m.quantity,
      }));
      await updateQuote({
        formula: {
          ...f!,
          finalCrewSize: newFinalCrewSize,
          actualJobDuration: newActualJobDuration,
          totalHours: newTotalHours,
          totalHourlyRate: newTotalHourlyRate,
          finalCostEstimate: newFinalCostEstimate,
        },
        crew: updatedCrew,
      });
      setEditingRates(false);
    } catch (err) {
      setRatesError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSavingRates(false);
    }
  }

  // Live-computed totals while editing rates
  const liveParsedCrew = crewEdits.map(m => ({ ...m, quantity: parseQty(m.qtyStr), hourlyRate: parseRate(m.rateStr) }));
  const liveTotalHourlyRate = liveParsedCrew.reduce((sum, m) => sum + m.quantity * m.hourlyRate, 0);

  // ── Hours edit handlers ───────────────────────────────────────────────────

  function startEditHours() {
    setHoursEdits({
      actualJobDurationStr: String(f!.actualJobDuration),
      finalTransitTimeHoursStr: String(f!.finalTransitTimeHours),
      travelTimeToJobHoursStr: String(f!.travelTimeToJobHours),
    });
    setHoursError('');
    setEditingHours(true);
  }

  function cancelEditHours() {
    setEditingHours(false);
    setHoursError('');
  }

  async function saveHours() {
    setSavingHours(true);
    setHoursError('');
    try {
      // Clamp on-site hours so crew size stays within [minCrew, maxCrew]
      // Round each component up to nearest 15 min (0.25 hrs)
      const rawJobDuration = parseHours(hoursEdits.actualJobDurationStr);
      const actualJobDuration = ceilQuarter(Math.max(minActualHours, rawJobDuration));
      const finalTransitTimeHours = ceilQuarter(parseHours(hoursEdits.finalTransitTimeHoursStr));
      const travelTimeToJobHours = ceilQuarter(parseHours(hoursEdits.travelTimeToJobHoursStr));
      const newTotalHours = ceilQuarter(actualJobDuration + finalTransitTimeHours + travelTimeToJobHours);
      // Derive the new crew size from the (possibly clamped) hours
      const newCrewFromHours = Math.max(minCrew, Math.min(maxCrew, Math.ceil(f!.manHours / actualJobDuration)));
      const newFinalCostEstimate = f!.totalHourlyRate * newTotalHours + (f!.materialsCost ?? 0);
      await updateQuote({
        formula: {
          ...f!,
          actualJobDuration,
          finalCrewSize: newCrewFromHours,
          finalTransitTimeHours,
          travelTimeToJobHours,
          totalHours: newTotalHours,
          finalCostEstimate: newFinalCostEstimate,
        },
      });
      setEditingHours(false);
    } catch (err) {
      setHoursError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSavingHours(false);
    }
  }

  // Live-computed total hours while editing
  const liveTotalHours = editingHours
    ? parseHours(hoursEdits.actualJobDurationStr) +
      parseHours(hoursEdits.finalTransitTimeHoursStr) +
      parseHours(hoursEdits.travelTimeToJobHoursStr)
    : f.totalHours;

  // ── Efficiency multiplier handlers ───────────────────────────────────────

  // The displayed multiplier for this quote
  const currentMultiplier = quote.efficiencyMultiplier ?? resolveOrgEfficiencyMultiplier(org, quote.type);

  function startEditMultiplier() {
    setMultiplierStr(String(currentMultiplier));
    setMultiplierError('');
    setEditingMultiplier(true);
  }

  function cancelEditMultiplier() {
    setEditingMultiplier(false);
    setMultiplierError('');
  }

  async function saveMultiplier() {
    const newMultiplier = Math.max(0, parseFloat(multiplierStr) || 1);
    setSavingMultiplier(true);
    setMultiplierError('');
    try {
      const effectiveCrew = quote!.crew ?? buildCrewFromOrgRates(orgRates);
      const formula = computeFormula(
        quote!,
        orgItems,
        quote!.travelTimeRate ?? resolveOrgTravelTimeRate(org, quote!.type),
        effectiveCrew,
        newMultiplier,
        org?.useBlendedTruckDriverRate,
        resolveOrgFixedTravelTimeHours(org, quote!.type),
      );

      // Compute the fingerprint as it will be AFTER saving efficiencyMultiplier,
      // then persist it so useFormulaEngine skips re-running on next navigation.
      const fingerprintAfterSave = getFormulaFingerprint({
        ...quote!,
        efficiencyMultiplier: newMultiplier,
      });

      // Compute crew quantities from the new formula so they are persisted correctly.
      const useBlended = org?.useBlendedTruckDriverRate ?? false;
      const isRes = quote!.type === 'residential';
      const supervisorQty = isRes ? 1 : (formula.method === 'Shuttle' ? 2 : 1);
      let qtyMap: Partial<Record<ResourceSlug, number>>;
      if (useBlended) {
        const truckDriverQty = formula.truckQuantity;
        const moverQty = Math.max(0, formula.finalCrewSize - supervisorQty - truckDriverQty);
        qtyMap = { supervisor: supervisorQty, truck_driver: truckDriverQty, mover: moverQty };
      } else if (isRes) {
        const moverQty = Math.max(0, formula.finalCrewSize - supervisorQty);
        qtyMap = { supervisor: supervisorQty, mover: moverQty, truck: formula.truckQuantity };
      } else {
        const formulaDriverQty = formula.driverQuantity ?? formula.truckQuantity;
        const moverQty = Math.max(0, formula.finalCrewSize - supervisorQty - formulaDriverQty);
        qtyMap = { supervisor: supervisorQty, driver: formulaDriverQty, mover: moverQty, truck: formula.truckQuantity };
      }
      const updatedCrew: QuoteCrewMember[] = effectiveCrew
        .filter(c => {
          if (useBlended) return c.slug !== 'driver' && c.slug !== 'truck';
          if (isRes) return c.slug !== 'driver';
          return true;
        })
        .map(c => ({
          ...c,
          quantity: qtyMap[c.slug] ?? 0,
        }));

      await updateQuote({
        efficiencyMultiplier: newMultiplier,
        formula: { ...formula, formulaInputsFingerprint: fingerprintAfterSave },
        crew: updatedCrew,
      });
      setEditingMultiplier(false);
    } catch (err) {
      setMultiplierError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSavingMultiplier(false);
    }
  }

  // ── Crew & Hours Balancer handler ─────────────────────────────────────────
  // Plain function (not useCallback): it sits below the early returns.

  const handleBalanceChange = async (
    newCrewSize: number,
    newActualHours: number,
    newTotalHours: number,
    _newCost: number,
  ) => {
    if (!isDraft || savingBalance) return;
    setSavingBalance(true);
    try {
      // Redistribute crew quantities based on new crew size
      const useBlended = org?.useBlendedTruckDriverRate ?? false;
      const isRes = quote!.type === 'residential';
      const currentCrew = quote!.crew ?? buildCrewFromFormula(f!, quote!.crew ?? [], quote!.type, useBlended);
      const supervisorQty = isRes ? 1 : (f!.method === 'Shuttle' ? 2 : 1);
      let qtyMap: Partial<Record<ResourceSlug, number>>;
      if (useBlended) {
        const truckDriverQty = f!.truckQuantity;
        const moverQty = Math.max(0, newCrewSize - supervisorQty - truckDriverQty);
        qtyMap = { supervisor: supervisorQty, truck_driver: truckDriverQty, mover: moverQty };
      } else if (isRes) {
        const moverQty = Math.max(0, newCrewSize - supervisorQty);
        qtyMap = { supervisor: supervisorQty, mover: moverQty, truck: f!.truckQuantity };
      } else {
        const balanceDriverQty = f!.driverQuantity ?? f!.truckQuantity;
        const moverQty = Math.max(0, newCrewSize - supervisorQty - balanceDriverQty);
        qtyMap = { supervisor: supervisorQty, driver: balanceDriverQty, mover: moverQty, truck: f!.truckQuantity };
      }
      // Recalculate hourly rate with new quantities
      const updatedCrew: QuoteCrewMember[] = currentCrew.map(c => ({
        slug: c.slug,
        name: c.name,
        hourlyRate: c.hourlyRate,
        quantity: qtyMap[c.slug] ?? (c as { quantity?: number }).quantity ?? 0,
      }));
      const newTotalHourlyRate = updatedCrew.reduce(
        (sum, c) => sum + (c.quantity ?? 0) * c.hourlyRate, 0,
      );
      const recalcCost = newTotalHourlyRate * newTotalHours + (f!.materialsCost ?? 0);

      await updateQuote({
        formula: {
          ...f!,
          finalCrewSize: newCrewSize,
          actualJobDuration: newActualHours,
          totalHours: newTotalHours,
          totalHourlyRate: newTotalHourlyRate,
          finalCostEstimate: recalcCost,
        },
        crew: updatedCrew,
      });
    } catch {
      // Silently fail — the UI will show stale data but no crash
    } finally {
      setSavingBalance(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────

  // Auto-calculated materials for the current inventory + rental length (what a recalculation would restore).
  const materialsAuto = computeQuoteMaterials(quote, orgItems);

  const crewWithQty = buildCrewFromFormula(f, quote.crew ?? [], quote.type, org?.useBlendedTruckDriverRate);
  const displayTotalHourlyRate = editingRates ? liveTotalHourlyRate : crewWithQty.reduce((sum, m) => sum + m.quantity * m.hourlyRate, 0);

  // Build per-resource rate map for scenario crew rebuilding (plain object: below the early returns, so no useMemo).
  // The balancer prices a crew as supervisors + drivers + movers + trucks. With a
  // blended "Truck + Driver" rate the crew has no separate driver/truck lines, so
  // map the blended rate onto the driver slot (one per truck) and zero the truck
  // slot; otherwise every scenario card silently dropped the truck-driver cost.
  const crewRateMapSource = new Map(crewWithQty.map(c => [c.slug, c.hourlyRate]));
  const scenarioUsesBlendedRate = org?.useBlendedTruckDriverRate ?? false;
  const crewRateMap = scenarioUsesBlendedRate
    ? {
        supervisor: crewRateMapSource.get('supervisor' as ResourceSlug) ?? 0,
        driver: crewRateMapSource.get('truck_driver' as ResourceSlug) ?? 0,
        mover: crewRateMapSource.get('mover' as ResourceSlug) ?? 0,
        truck: 0,
      }
    : {
        supervisor: crewRateMapSource.get('supervisor' as ResourceSlug) ?? 0,
        driver: crewRateMapSource.get('driver' as ResourceSlug) ?? 0,
        mover: crewRateMapSource.get('mover' as ResourceSlug) ?? 0,
        truck: crewRateMapSource.get('truck' as ResourceSlug) ?? 0,
      };
  const scenarioDriverQty = scenarioUsesBlendedRate ? f.truckQuantity : driverQty;

  return (
    <div className="px-4 pt-6 pb-10 md:mx-auto md:max-w-3xl md:px-6">
      {/* Back */}
      <button
        type="button"
        onClick={() => navigate(backUrl)}
        className="flex items-center gap-1 text-small text-slate hover:text-midnight mb-4
                   transition-colors -ml-1"
      >
        <ChevronLeft size={16} />
        {quote.customerCompanyName}
      </button>

      <h1 className="font-display text-h1 text-midnight mb-5">Estimate Breakdown</h1>

      {quote.quoteStatus !== 'draft' && <ReadOnlyBanner status={quote.quoteStatus} />}

      {f.isFlatRate ? (
        <FlatRateBreakdown
          f={f}
          symbol={symbol}
          isDraft={isDraft}
          materialsAuto={materialsAuto}
          updateQuote={updateQuote}
        />
      ) : (
      <div className="flex flex-col gap-4">

        {/* ── Minimum crew note ── */}
        <div className="flex items-start gap-2.5 px-4 py-3 rounded-xl bg-sky-50 border border-sky-200">
          <Info size={15} className="shrink-0 text-sky-600 mt-0.5" />
          <p className="text-[13px] text-sky-900 leading-snug">
            {isResQuote
              ? `Residential move with ${f.truckQuantity} truck${f.truckQuantity !== 1 ? 's' : ''}. `
                + `Crew: 1 supervisor (who drives) + ${Math.max(0, f.finalCrewSize - 1)} mover${f.finalCrewSize - 1 !== 1 ? 's' : ''}. `
                + `Min crew ${minCrew}, max ${maxCrew}.`
              : f.method === 'Internal'
                ? `This is an Internal move — no trucks or drivers needed. `
                  + `Crew: 1 supervisor + ${Math.max(0, f.finalCrewSize - 1)} mover${Math.max(0, f.finalCrewSize - 1) !== 1 ? 's' : ''}. `
                  + `Max crew is ${maxCrew} (origin optimum crew size).`
                : f.method === 'Shuttle'
                  ? `This is a Shuttle move with ${f.truckQuantity} truck${f.truckQuantity !== 1 ? 's' : ''} `
                    + `and ${driverQty} driver${driverQty !== 1 ? 's' : ''}. `
                    + `Max crew is ${maxCrew} (combined optimum ${f.combinedOptimumCrewSize} + ${shuttleTableDrivers} from transit table). `
                    + `Crew composition: 2 supervisors + ${driverQty} driver${driverQty !== 1 ? 's' : ''} + movers.`
                  : `This is a Load & Go move with ${f.truckQuantity} truck${f.truckQuantity !== 1 ? 's' : ''}. `
                    + `Minimum crew size is ${minCrew} — `
                    + `each truck needs a driver, so you need at least ${f.truckQuantity} crew member${f.truckQuantity !== 1 ? 's' : ''} `
                    + `(1 supervisor + ${Math.max(0, f.truckQuantity - 1)} driver${Math.max(0, f.truckQuantity - 1) !== 1 ? 's' : ''}).`}
          </p>
        </div>

        {/* ── Scenario Explorer ── */}
        <CrewHoursBalancerScenarios
          additionalCost={f.materialsCost ?? 0}
          manHours={f.manHours}
          crewSize={f.finalCrewSize}
          actualHours={f.actualJobDuration}
          fixedHours={f.finalTransitTimeHours + f.travelTimeToJobHours}
          totalHourlyRate={displayTotalHourlyRate}
          symbol={symbol}
          editable={isDraft}
          minCrew={minCrew}
          maxCrew={maxCrew}
          shiftLengthHours={shiftLengthHours}
          method={f.method}
          truckQuantity={f.truckQuantity}
          driverQuantity={scenarioDriverQty}
          crewRates={crewRateMap}
          onChange={handleBalanceChange}
        />

        {/* ── Fine-Tune Quote (linked sliders) ── */}
        <FineTuneQuoteSliders
          additionalCost={f.materialsCost ?? 0}
          manHours={f.manHours}
          crewSize={f.finalCrewSize}
          actualHours={f.actualJobDuration}
          fixedHours={f.finalTransitTimeHours + f.travelTimeToJobHours}
          totalHourlyRate={displayTotalHourlyRate}
          symbol={symbol}
          editable={isDraft}
          minCrew={minCrew}
          maxCrew={maxCrew}
          shiftLengthHours={shiftLengthHours}
          method={f.method}
          truckQuantity={f.truckQuantity}
          driverQuantity={scenarioDriverQty}
          crewRates={crewRateMap}
          shuttleDrivers={shuttleTableDrivers}
          onChange={handleBalanceChange}
        />

        {/* ── Total Hourly Rate ── */}
        <BreakdownCard
          title="Total Hourly Rate"
          headerValue={`${symbol}${fmt(displayTotalHourlyRate, 2)}/h`}
          action={isDraft && !editingRates ? <EditButton onClick={startEditRates} /> : undefined}
        >
          {editingRates ? (
            <>
              {crewEdits.map((m, i) => (
                <div
                  key={m.slug}
                  className="flex items-center gap-2 py-2.5 border-b border-stone last:border-0"
                >
                  <p className="text-small font-medium text-midnight flex-1 min-w-0 truncate">{m.name}</p>
                  <div className="flex items-center gap-1 shrink-0">
                    <InlineInput
                      value={m.qtyStr}
                      onChange={v => updateCrewEntry(i, 'qtyStr', v)}
                      width="w-10"
                    />
                    <span className="text-[12px] text-slate">×</span>
                    <span className="text-[12px] text-slate">{symbol}</span>
                    <InlineInput
                      value={m.rateStr}
                      onChange={v => updateCrewEntry(i, 'rateStr', v)}
                      width="w-16"
                      step="0.01"
                    />
                    <span className="text-[12px] text-slate">/h</span>
                  </div>
                </div>
              ))}
              <SaveCancelRow
                onSave={saveRates}
                onCancel={cancelEditRates}
                saving={savingRates}
                error={ratesError}
              />
            </>
          ) : crewWithQty.length > 0 ? (
            <>
              {crewWithQty.map(m => (
                <BreakdownRow
                  key={m.slug}
                  label={m.name}
                  right={`${m.quantity} × ${symbol}${fmt(m.hourlyRate, 2)}/h`}
                />
              ))}
              {/* Zero-rate warning: lists which billed resources are priced at $0 */}
              <RateCardHint readiness={readiness} isOrgAdmin={isOrgAdmin} className="mt-2" />
            </>
          ) : (
            <>
              <p className="py-4 text-[13px] text-slate text-center italic">
                No crew rates loaded — open Quote Details to recalculate.
              </p>
              <RateCardHint readiness={readiness} isOrgAdmin={isOrgAdmin} />
            </>
          )}
        </BreakdownCard>

        {/* ── Total Billable Hours ── */}
        <BreakdownCard
          title="Total Billable Hours"
          headerValue={fmtHours(liveTotalHours)}
          action={isDraft && !editingHours ? <EditButton onClick={startEditHours} /> : undefined}
        >
          {editingHours ? (
            <>
              {(
                [
                  { key: 'actualJobDurationStr', label: 'On-site Hours', sub: 'Actual Job Duration' },
                  { key: 'finalTransitTimeHoursStr', label: 'Transit Time', sub: 'Final Transit Time (origin → destination)' },
                  { key: 'travelTimeToJobHoursStr', label: 'Travel to Job Site', sub: 'Travel Time to Job' },
                ] as const
              ).map(row => (
                <div
                  key={row.key}
                  className="flex items-center gap-4 py-2.5 border-b border-stone last:border-0"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-small font-medium text-midnight">{row.label}</p>
                    <p className="text-[11px] text-slate mt-0.5">{row.sub}</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <InlineInput
                      value={hoursEdits[row.key]}
                      onChange={v => setHoursEdits(prev => ({ ...prev, [row.key]: v }))}
                      width="w-16"
                      step="0.1"
                    />
                    <span className="text-[12px] text-slate">hrs</span>
                  </div>
                </div>
              ))}
              <SaveCancelRow
                onSave={saveHours}
                onCancel={cancelEditHours}
                saving={savingHours}
                error={hoursError}
              />
            </>
          ) : (
            <>
              <BreakdownRow
                label="On-site Hours"
                sub="Actual Job Duration"
                right={fmtHours(f.actualJobDuration)}
              />
              <BreakdownRow
                label="Transit Time"
                sub="Final Transit Time (origin → destination)"
                right={fmtHours(f.finalTransitTimeHours)}
              />
              <BreakdownRow
                label="Travel to Job Site"
                sub="Travel Time to Job"
                right={fmtHours(f.travelTimeToJobHours)}
              />
            </>
          )}
        </BreakdownCard>

        {/* ── Materials ── */}
        <MaterialsCard f={f} symbol={symbol} isDraft={isDraft} auto={materialsAuto} updateQuote={updateQuote} />

        {/* ── Efficiency Multiplier ── */}
        <BreakdownCard
          title="Efficiency Multiplier"
          headerValue={fmt(editingMultiplier ? (Math.max(0, parseFloat(multiplierStr) || 1)) : currentMultiplier, 2)}
          action={isDraft && !editingMultiplier ? <EditButton onClick={startEditMultiplier} /> : undefined}
        >
          {editingMultiplier ? (
            <>
              <div className="flex items-center gap-4 py-2.5">
                <p className="text-small font-medium text-midnight flex-1 min-w-0">Multiplier</p>
                <InlineInput
                  value={multiplierStr}
                  onChange={setMultiplierStr}
                  width="w-20"
                  step="0.01"
                />
              </div>
              <p className="text-[11px] text-slate pb-1">
                Adjusts final man-hours. Changing this will re-run the formula engine.
              </p>
              <SaveCancelRow
                onSave={saveMultiplier}
                onCancel={cancelEditMultiplier}
                saving={savingMultiplier}
                error={multiplierError}
              />
            </>
          ) : (
            <BreakdownRow
              label="Multiplier"
              sub="Applied to base man-hours. Edit to re-run the formula."
              right={fmt(currentMultiplier, 2)}
            />
          )}
        </BreakdownCard>

        {/* ── Tax ── */}
        {(quote.laborTaxRate != null || quote.requiresManualTaxReview) && f && (
          <BreakdownCard title="Tax" headerValue="">
            <TaxBreakdown
              subtotal={f.finalCostEstimate}
              taxState={quote.taxState}
              laborTaxRate={quote.laborTaxRate}
              taxAmount={quote.taxAmount}
              materialsTaxRate={quote.materialsTaxRate}
              materialsCost={f.materialsCost}
              totalWithTax={quote.taxAmount != null ? f.finalCostEstimate + quote.taxAmount : undefined}
              requiresManualTaxReview={quote.requiresManualTaxReview}
              symbol={symbol}
            />
          </BreakdownCard>
        )}

      </div>
      )}
    </div>
  );
}

// ── Flat Rate Breakdown ───────────────────────────────────────────────────────

function FlatRateBreakdown({
  f,
  symbol,
  isDraft,
  materialsAuto,
  updateQuote,
}: {
  f: QuoteFormula;
  symbol: string;
  isDraft: boolean;
  materialsAuto: QuoteMaterialsSummary;
  updateQuote: (patch: Record<string, unknown>) => Promise<unknown>;
}) {
  const [editing, setEditing] = useState(false);
  const [roomPriceStr, setRoomPriceStr] = useState('');
  const [addonEdits, setAddonEdits] = useState<{ slug: string; label: string; amountStr: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const addons = f.flatRateAddons ?? [];
  const total = f.flatRateTotal ?? f.finalCostEstimate;

  function startEdit() {
    setRoomPriceStr(String(f.flatRateRoomPrice ?? 0));
    setAddonEdits(addons.map(a => ({ slug: a.slug, label: a.label, amountStr: String(a.amount) })));
    setError('');
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setError('');
  }

  async function saveEdit() {
    setSaving(true);
    setError('');
    try {
      const newRoomPrice = Math.max(0, parseFloat(roomPriceStr) || 0);
      const newAddons = addonEdits.map(a => ({
        slug: a.slug,
        label: a.label,
        amount: parseFloat(a.amountStr) || 0,
      }));
      const newTotal = newRoomPrice + newAddons.reduce((sum, a) => sum + a.amount, 0);
      await updateQuote({
        formula: {
          ...f,
          flatRateRoomPrice: newRoomPrice,
          flatRateAddons: newAddons,
          flatRateTotal: newTotal,
          finalCostEstimate: newTotal + (f.materialsCost ?? 0),
        },
      });
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  // Live total while editing
  const liveTotal = editing
    ? (parseFloat(roomPriceStr) || 0) + addonEdits.reduce((sum, a) => sum + (parseFloat(a.amountStr) || 0), 0)
    : total;

  return (
    <div className="flex flex-col gap-4">
      {/* Info note */}
      <div className="flex items-start gap-2.5 px-4 py-3 rounded-xl bg-sky-50 border border-sky-200">
        <Info size={15} className="shrink-0 text-sky-600 mt-0.5" />
        <p className="text-[13px] text-sky-900 leading-snug">
          This is a flat rate quote. Pricing is based on room count ({f.flatRateBedroomCount ?? '—'} bedrooms)
          plus add-on adjustments.
          {f.finalCrewSize > 0 && ` Recommended crew: ${f.finalCrewSize}, trucks: ${f.truckQuantity}.`}
        </p>
      </div>

      {/* Flat Rate Line Items */}
      <BreakdownCard
        title="Flat Rate Pricing"
        headerValue={`${symbol}${fmt(liveTotal, 0)}`}
        action={isDraft && !editing ? <EditButton onClick={startEdit} /> : undefined}
      >
        {editing ? (
          <>
            {/* Room price */}
            <div className="flex items-center gap-4 py-2.5 border-b border-stone">
              <div className="flex-1 min-w-0">
                <p className="text-small font-medium text-midnight">
                  {f.flatRateRoomLabel ?? 'Room Rate'}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <span className="text-[12px] text-slate">{symbol}</span>
                <InlineInput
                  value={roomPriceStr}
                  onChange={setRoomPriceStr}
                  width="w-20"
                  step="1"
                />
              </div>
            </div>
            {/* Addon edits */}
            {addonEdits.map((addon, i) => (
              <div
                key={addon.slug}
                className="flex items-center gap-4 py-2.5 border-b border-stone last:border-0"
              >
                <p className="text-small font-medium text-midnight flex-1 min-w-0">{addon.label}</p>
                <div className="flex items-center gap-1 shrink-0">
                  <span className="text-[12px] text-slate">{symbol}</span>
                  <InlineInput
                    value={addon.amountStr}
                    onChange={v => setAddonEdits(prev => prev.map((a, j) => j === i ? { ...a, amountStr: v } : a))}
                    width="w-20"
                    step="1"
                  />
                </div>
              </div>
            ))}
            <SaveCancelRow
              onSave={saveEdit}
              onCancel={cancelEdit}
              saving={saving}
              error={error}
            />
          </>
        ) : (
          <>
            <BreakdownRow
              label={f.flatRateRoomLabel ?? 'Room Rate'}
              sub={`${f.flatRateBedroomCount ?? '—'} bedroom flat rate`}
              right={`${symbol}${fmt(f.flatRateRoomPrice ?? 0, 0)}`}
            />
            {addons.map(addon => (
              <BreakdownRow
                key={addon.slug}
                label={addon.label}
                right={`${addon.amount >= 0 ? '+' : ''}${symbol}${fmt(addon.amount, 0)}`}
              />
            ))}
          </>
        )}
      </BreakdownCard>

      {/* Materials */}
      <MaterialsCard f={f} symbol={symbol} isDraft={isDraft} auto={materialsAuto} updateQuote={updateQuote} />

      {/* Scheduling Info */}
      <BreakdownCard
        title="Scheduling"
        headerValue={`${f.finalCrewSize} crew · ${f.truckQuantity} truck${f.truckQuantity !== 1 ? 's' : ''}`}
      >
        <BreakdownRow label="Crew Size" right={String(f.finalCrewSize)} />
        <BreakdownRow label="Trucks" right={String(f.truckQuantity)} />
        <BreakdownRow
          label="Estimated Duration"
          sub="On-site + travel + transit"
          right={fmtHours(f.totalHours)}
        />
      </BreakdownCard>
    </div>
  );
}
